import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Container, Ship, FileCheck2 } from "lucide-react";
import PortModal from "@/components/PortModal";

const IMG =
  "https://images.pexels.com/photos/38393808/pexels-photo-38393808.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PORTS = [
  { code: "CNQIN", name: "Qingdao", region: "China" },
  { code: "ILASH", name: "Ashdod", region: "Israel · Tel Aviv" },
  { code: "AEJEA", name: "Jebel Ali", region: "UAE" },
  { code: "INMUN", name: "Mundra", region: "India" },
  { code: "INNSA", name: "Nhava Sheva", region: "India" },
  { code: "THLCH", name: "Laem Chabang", region: "Thailand" },
  { code: "TRIST", name: "Istanbul", region: "Türkiye" },
];

const CARDS = [
  { icon: Container, title: "Container Logistics", body: "Containerized freight movements, shipping line contracts, and port clearance procedures — managed end to end." },
  { icon: Ship, title: "Key Shipping Routes", body: "First-hand command of transit times and customs protocols between major global ports and trading hubs." },
  { icon: FileCheck2, title: "Customs & HS Compliance", body: "Tariff classification, documentation verification, and compliance for base metal ingots and extrusions." },
];

export default function Logistics() {
  const ref = useRef(null);
  const [activePort, setActivePort] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="logistics" ref={ref} data-testid="logistics-section" className="py-20 lg:py-32 bg-[#0D1119] border-y border-[#283244]/60 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead
          index="/04"
          overline="Global Freight & Trade Logistics"
          title="Seamless execution of international container logistics across major trading hubs."
          testId="logistics-head"
        />

        <div className="relative rounded-md overflow-hidden border border-[#283244] mb-12" data-testid="logistics-image-band">
          <motion.img
            style={{ y: imgY }}
            src={IMG}
            alt="Container port at dusk with cranes"
            className="w-full h-[380px] lg:h-[460px] object-cover scale-125"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12] via-[#0A0D12]/45 to-[#0A0D12]/25" />
          <div className="absolute inset-x-0 bottom-0 p-6 lg:p-10">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#F3B700] mb-4">Active Port Network — tap a port for route intel</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="port-grid">
              {PORTS.map((p, i) => (
                <Reveal key={p.code} delay={i * 0.05}>
                  <button
                    onClick={() => setActivePort(p)}
                    className="w-full text-left group border border-[#F4F6F8]/15 bg-[#0A0D12]/60 backdrop-blur-md rounded-sm p-3 hover:border-[#E06D28]/70 transition-colors duration-300 cursor-pointer"
                    data-testid={`port-${p.code.toLowerCase()}`}
                  >
                    <div className="font-mono-tech text-[10px] text-[#F07D38] tracking-widest">{p.code}</div>
                    <div className="font-display font-bold text-sm text-[#F4F6F8] mt-1 group-hover:text-[#F07D38] transition-colors duration-300">{p.name}</div>
                    <div className="text-[11px] text-[#94A3B8]">{p.region}</div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div data-testid={`logistics-card-${i}`} className="card-industrial p-7 lg:p-8 h-full group">
                <span className="w-11 h-11 rounded-sm bg-[#E06D28]/10 border border-[#E06D28]/30 flex items-center justify-center text-[#F07D38] transition-transform duration-300 group-hover:scale-110 mb-5">
                  <c.icon size={20} />
                </span>
                <h3 className="font-display text-lg font-bold text-[#F4F6F8]">{c.title}</h3>
                <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="hs-code-chips">
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#64748B] mr-2">Tariff Classifications</span>
            {["HS 7404 · Copper Waste & Scrap", "HS 7601 · Unwrought Aluminum", "HS 7604 · Aluminum Bars & Profiles"].map((hs) => (
              <span key={hs} className="font-mono-tech text-xs text-[#F07D38] border border-[#E06D28]/40 bg-[#E06D28]/10 px-4 py-2 rounded-full">
                {hs}
              </span>
            ))}
          </div>
        </Reveal>
      </div>

      <PortModal port={activePort} onClose={() => setActivePort(null)} />
    </section>
  );
}
